import { FC, useEffect, useRef, useState, Suspense, SetStateAction, Dispatch } from 'react';
import { AboutTheSkill } from '../AboutTheSkill/AboutTheSkill';
import { useLrnSessionBySkill } from '../../custom-hooks/useLrnSession';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { WorkedExample } from '../WorkedExample/WorkedExample';
import { AssessmentComponent } from '../AssessmentComponent/AssessmentComponent';
import { PreloadedQuery, useLazyLoadQuery, usePreloadedQuery } from 'react-relay';
import { getSupportSkills } from '../../graphQLOperations/SkillResource';
import { SkillResourceSteps } from '../../models/Timeline';
import { AttemptInformation } from '../../models/AttemptInformation';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { PreviousAndNextSSS } from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { SupportSkills } from '../../models/SupportSkills';
import { SkillResourceGetSupportSkillsSkillStatesQuery } from '../../graphQLOperations/__generated__/SkillResourceGetSupportSkillsSkillStatesQuery.graphql';
import { getHighAndMediumSupportSkillsFromQuery } from '../../businessLogicService/getHighAndMediumSupportSkills';
import { SkillsPresignedURLsQuery } from '../../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';
import { selectedStudentSkillStates } from '../../redux/reducers/studentSkillsSlice';
import { useCurrentSkillResult } from '../../custom-hooks/useCurrentSkillResult';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { getPresignedURLsForSkill } from '../../graphQLOperations/Skills';
import { UserActionTrackingProvider } from '../../contexts/UserActionTrackingContext';
import { useFlag } from '@unleash/proxy-client-react';
import TimeLineWrapper from './TimeLineWrapper';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { StudentSingleSkillQuery$data } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import { CurrentStatus, PreviousStatus } from '../StudentSingleSkill/StudentSingleSkill';
import SkillResourceHeader from './SkillResourceHeader';
import { GoalValue } from '../WeeklyGoal/WeeklyGoalWrapper';
import { SkillResourceAvatarPanel } from '../../experiments/TEAME-223/SkillResourceAvatarPanel';
import { useCondition } from '../../experiments/ExperimentsProvider';
import { TEAME_223_FLAG_ID, TEAME_223_TREATMENT } from '../../experiments/TEAME-223/constants';
import { LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG } from '../../contexts/WeeklyGoalContext';
import { PracticeProblemsExperimentAssess } from '../../experiments/TEAMC-3228/PracticeProblemsExperimentAssess';
import { PracticeProblems } from '../PracticeProblems/PracticeProblems';
import { useParams } from 'react-router-dom';
import { TEAMC_3228_FLAG, TEAMC_3228_FLAG_VARIANTS } from '../../experiments/TEAMC-3228/constants';
import { useFeatureFlagVariantKey } from '@posthog/react';

interface Props {
  setCurrentStatus: (value: { classname: string; status: string | undefined } | null) => void;
  studentStateInfo: StudentSingleSkillQuery$data['skillStatesByStudentId'];
  previousAttempts: PreviousAttempts[];
  setPreviousAttempts: Dispatch<SetStateAction<PreviousAttempts[]>>;
  setAttemptInformation: (info: AttemptInformation | null) => void;
  attemptInformation: AttemptInformation | null;
  sssAfterTakeAssessment: PreviousAndNextSSS;
  currentStep: SkillResourceSteps | null;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  setSkipNewActivityId: Dispatch<SetStateAction<boolean>>;
  skipNewActivityId: boolean;
  skillsPresignedURLsQueryReference: PreloadedQuery<SkillsPresignedURLsQuery, Record<string, unknown>>;
  setPreviousStatus: Dispatch<SetStateAction<PreviousStatus[]>>;
  previousStatus: PreviousStatus[];
  currentStatus: CurrentStatus | null;
  allUserActions: StudentSingleSkillQuery$data['allUserActions'];
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
  assessmentInProgress: boolean;
}

export const SkillResource: FC<Props> = ({
  setCurrentStatus,
  studentStateInfo,
  previousAttempts,
  setPreviousAttempts,
  setAttemptInformation,
  attemptInformation,
  sssAfterTakeAssessment,
  currentStep,
  setCurrentStep,
  setSkipNewActivityId,
  skipNewActivityId,
  skillsPresignedURLsQueryReference,
  setPreviousStatus,
  previousStatus,
  currentStatus,
  allUserActions,
  setAssessmentInProgress,
  assessmentInProgress,
}) => {
  const { skillCode: skillNumber } = useParams();
  const timeoutController = useRef<NodeJS.Timeout | null>(null);
  const timelineContentRef = useRef<HTMLElement | null>(null);
  // Frozen at mount. Passing the challenge moves the skill to PASSED_VIA_ASSESSMENT, so this is
  // the only point at which a prior inference is still readable — and the weekly goal needs to
  // know about it. Held here rather than in AssessmentComponent, which remounts per Learnosity
  // session and so would re-read a post-attempt substate on a retake.
  const preAttemptSubstateId = useRef<string | null>(studentStateInfo[0]?.skillSubstate?.id ?? null);
  const user = useAppSelector(selectUser);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const dashboardStudentSkillStates = useAppSelector(selectedStudentSkillStates) || [];
  const [mediumRelevanceSupportSkills, setMediumRelevanceSupportSkills] = useState<SupportSkills[]>([]);
  const [highRelevanceSupportSkills, setHighRelevanceSupportSkills] = useState<SupportSkills[]>([]);
  const isFutureSelfAvatarEnabled = useCondition(TEAME_223_FLAG_ID) === TEAME_223_TREATMENT;
  const [expanded, setExpanded] = useState<boolean>(false);
  const [learnosityPPKey, setLearnosityPPKey] = useState<number | null>(null);
  const [disabledReviewModal, setDisabledReviewModal] = useState<boolean>(false);
  const isWeeklyGoalEnabled = !useFlag(LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG);
  const [weeklyGoalOverrideData, setWeeklyGoalOverrideData] = useState<GoalValue | null>(null);
  const practiceProblemsExperimentVariant = useFeatureFlagVariantKey(TEAMC_3228_FLAG);
  // Use shared hook to get the most up-to-date skill result (including post-assessment state)
  const currentSkillResult = useCurrentSkillResult(studentStateInfo[0] as unknown as StudentSkillStates);

  const { data, refetchLrnSession, loadPreviousSesssion, setAssessmentState } = useLrnSessionBySkill(
    studentStateInfo[0].skill?.id || '',
    skipNewActivityId,
  );

  /*
  remove Assessment actions missing a sessionId because with the mutation retry for assessment user_action some wrong data was created in our database
  Once TEAMC-1001 gets merged this should not happen again, and sanitizedTimeline can be removed or not
  */
  const supportedSkillsQuery = useLazyLoadQuery<SkillResourceGetSupportSkillsSkillStatesQuery>(
    getSupportSkills,
    {
      skillId: studentStateInfo[0]?.skill?.id || '',
      studentId: `StudentNode:${user?.id}`,
      targetGroupId: targetGroupRedux?.id || '',
    },
    {
      fetchPolicy: 'network-only',
    },
  );

  let presignedURLsForSkill: SkillsPresignedURLsQuery['response']['presignedURLsForSkill'] | undefined;
  let presignedUrlsError: Error | null = null;

  try {
    ({ presignedURLsForSkill } = usePreloadedQuery<SkillsPresignedURLsQuery>(
      getPresignedURLsForSkill,
      skillsPresignedURLsQueryReference as PreloadedQuery<SkillsPresignedURLsQuery>,
    ));
  } catch (error) {
    // Depending on network speeds and load order, the query may not have resolved yet.
    // Relay Suspense can throw a Promise while loading; rethrow so Suspense can handle it as expected.
    if (error && typeof (error as Promise<unknown>).then === 'function') {
      throw error;
    } else {
      presignedUrlsError = error as Error;
    }
  }

  const hasPresignedUrlsError = Boolean(presignedUrlsError);

  useEffect(() => {
    if (currentStep === SkillResourceSteps.SKILL_CHALLENGE && previousAttempts.length) {
      // open skill challenge option in review mode using previous attempt
      loadPreviousSesssion(previousAttempts[0].sessionId ?? '', previousAttempts[0].activityId, true);
      setAttemptInformation({
        totalScore: previousAttempts[0].score,
        scores:
          previousAttempts[0]?.items?.map(el => {
            return {
              id: el.id,
              score: el.score,
            };
          }) ?? [],
      });
    }
  }, []);

  useEffect(() => {
    if (supportedSkillsQuery) {
      const { highSupportSkills, mediumSupportSkills } = getHighAndMediumSupportSkillsFromQuery(supportedSkillsQuery);
      setMediumRelevanceSupportSkills(mediumSupportSkills);
      setHighRelevanceSupportSkills(highSupportSkills);
    }
  }, [supportedSkillsQuery]);

  useEffect(() => {
    return () => {
      if (timeoutController.current) {
        clearTimeout(timeoutController.current);
      }
    };
  }, []);

  useEffect(() => {
    const currentSkillState = studentStateInfo[0];
    if (!currentSkillState || !user?.id) {
      return;
    }

    if (isWeeklyGoalEnabled) {
      setWeeklyGoalOverrideData({
        code: currentSkillState.skill?.code || '',
        name: currentSkillState.skill?.name || '',
        skillType: dashboardStudentSkillStates
          ? dashboardStudentSkillStates?.find(s => s.skill.code === currentSkillState.skill?.code)?.targetSkillType ||
            'Support'
          : 'Support',
      });
    }
  }, [studentStateInfo[0]?.skill?.id, user?.id]);

  useEffect(() => {
    const skillName = studentStateInfo[0].skill?.name?.trim();
    document.title = skillName ? `Teach to One Roadmaps | Skill | ${skillName}` : 'Teach to One Roadmaps | Skill';
  }, [studentStateInfo[0].skill?.name]);
  useEffect(() => {
    if (!currentStep) return;
    const focusCurrentTimelineSection = () => {
      const section = timelineContentRef.current;
      if (!section) return;
      section.focus({ preventScroll: true });
    };
    requestAnimationFrame(focusCurrentTimelineSection);
    const focusTimeout = window.setTimeout(focusCurrentTimelineSection, 300);
    return () => {
      window.clearTimeout(focusTimeout);
    };
  }, [currentStep, learnosityPPKey]);
  return (
    <div className="xl:tw-grid">
      {!assessmentInProgress && (
        <SkillResourceHeader
          currentStatus={currentStatus}
          previousAttempts={previousAttempts}
          currentSkillResult={currentSkillResult}
          previousStatus={previousStatus}
          skillName={studentStateInfo[0].skill?.name ?? ''}
          allUserActions={allUserActions}
          expanded={expanded}
          setExpanded={setExpanded}
          disabledReviewModal={disabledReviewModal}
        />
      )}

      <UserActionTrackingProvider skillId={studentStateInfo[0]?.skill?.id || ''} userId={user?.id?.toString() || ''}>
        <div className="tw-flex lg:tw-flex-row tw-flex-col tw-gap-4">
          {!assessmentInProgress && (
            <div className="nc_sidebar-timeline tw-w-full lg:tw-flex lg:tw-flex-col tw-py-0 lg:tw-w-4/12 2xl:tw-w-3/12 xl:tw-px-5">
              {isFutureSelfAvatarEnabled && <SkillResourceAvatarPanel />}
              <TimeLineWrapper
                skillId={studentStateInfo[0]?.skill?.id || ''}
                setLearnosityPPKey={setLearnosityPPKey}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                setSkipNewActivityId={setSkipNewActivityId}
                refetchLrnSession={refetchLrnSession}
                setExpanded={setExpanded}
                setDisabledReviewModal={setDisabledReviewModal}
                setAssessmentInProgress={setAssessmentInProgress}
              />
            </div>
          )}

          <div
            id="main-content-div"
            className={`tw-w-full tw-flex tw-flex-col ${
              currentStep === SkillResourceSteps.SKILL_CHALLENGE ? 'tw-w-full' : 'lg:tw-w-9/12 2xl:tw-w-9/12'
            } tw-pt-0 `}
          >
            <Suspense fallback={<ThreeDotLoader dark />}>
              <>
                {currentStep === SkillResourceSteps.ABOUT_THE_SKILL && (
                  <section ref={timelineContentRef} id="about-the-skill" tabIndex={-1}>
                    <AboutTheSkill
                      midSupportSkills={mediumRelevanceSupportSkills}
                      highSupportSkills={highRelevanceSupportSkills}
                      studentStateInfo={studentStateInfo}
                    />
                  </section>
                )}
                {currentStep === SkillResourceSteps.WORKED_EXAMPLE && (
                  <section ref={timelineContentRef} id="worked-example" tabIndex={-1}>
                    {hasPresignedUrlsError ? (
                      <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700">
                        Worked example video resources are unavailable right now. Please try again later.
                      </div>
                    ) : (
                      <WorkedExample
                        videoUrl={presignedURLsForSkill?.workedExampleVideoUrl}
                        captionUrlEnglish={presignedURLsForSkill?.workedExampleVideoEnglishCaptionUrl}
                        captionUrlSpanish={presignedURLsForSkill?.workedExampleVideoSpanishCaptionUrl}
                      />
                    )}
                  </section>
                )}
                {currentStep === SkillResourceSteps.PRACTICE_PROBLEMS && studentStateInfo[0].skill?.id && (
                  <section ref={timelineContentRef} id="practice-problems" tabIndex={-1}>
                    {/*
                      Anything other than a definite `true` falls through to control, including
                      the `undefined` the hook returns before PostHog answers. Do not block on a
                      value arriving: initializePostHog bails out when VITE_POSTHOG_ENABLED is
                      not 'true', so posthog.init never runs and the flag stays undefined for
                      the life of the page. That is the default for manual frontend deploys and
                      for local dev, not an edge case.
                    */}
                    {practiceProblemsExperimentVariant === TEAMC_3228_FLAG_VARIANTS.TREATMENT_1 ||
                    practiceProblemsExperimentVariant === TEAMC_3228_FLAG_VARIANTS.TREATMENT_2 ? (
                      <PracticeProblemsExperimentAssess
                        key={learnosityPPKey}
                        skillId={studentStateInfo[0]?.skill?.id}
                        skillCode={skillNumber || ''}
                        refetchLrnSession={refetchLrnSession}
                        setCurrentStep={setCurrentStep}
                        setSkipNewActivityId={setSkipNewActivityId}
                        learnosityPPKey={learnosityPPKey}
                        setAssessmentInProgress={setAssessmentInProgress}
                        practiceProblemsExperimentVariant={practiceProblemsExperimentVariant}
                      />
                    ) : (
                      <PracticeProblems
                        key={learnosityPPKey}
                        skillId={studentStateInfo[0].skill.id}
                        skillCode={skillNumber || ''}
                        refetchLrnSession={refetchLrnSession}
                        setCurrentStep={setCurrentStep}
                        setSkipNewActivityId={setSkipNewActivityId}
                        learnosityPPKey={learnosityPPKey}
                        setAssessmentInProgress={setAssessmentInProgress}
                      />
                    )}
                  </section>
                )}
                {currentStep === SkillResourceSteps.SKILL_CHALLENGE && data?.sessionId && (
                  <section ref={timelineContentRef} id="skill-challenge" tabIndex={-1}>
                    <AssessmentComponent
                      key={data.sessionId}
                      workedExampleUrl={presignedURLsForSkill?.workedExampleVideoUrl}
                      title={studentStateInfo[0].skill?.name || ''}
                      setAttemptInformation={setAttemptInformation}
                      attemptInformation={attemptInformation}
                      setAssessmentState={setAssessmentState}
                      setPreviousAttempts={setPreviousAttempts}
                      previousAttempts={previousAttempts}
                      setCurrentStatus={setCurrentStatus}
                      lrnSessionId={data}
                      setCurrentStep={setCurrentStep}
                      essentialSkills={highRelevanceSupportSkills}
                      sssAfterTakeAssessment={sssAfterTakeAssessment}
                      refetchLrnSession={refetchLrnSession}
                      skipNewActivityId={skipNewActivityId}
                      setSkipNewActivityId={setSkipNewActivityId}
                      setPreviousStatus={setPreviousStatus}
                      setDisabledReviewModal={setDisabledReviewModal}
                      weeklyGoalOverrideData={weeklyGoalOverrideData}
                      setAssessmentInProgress={setAssessmentInProgress}
                      assessmentInProgress={assessmentInProgress}
                      skillId={studentStateInfo[0].skill?.id}
                      preAttemptSubstateId={preAttemptSubstateId.current}
                    />
                  </section>
                )}
              </>
            </Suspense>
          </div>
        </div>
      </UserActionTrackingProvider>
    </div>
  );
};
